import React, {FC, useCallback, useMemo} from 'react';
import {Scalars} from '.cache/__types__';
import {Modal} from '@components/Modal';
import {Typography} from '@components/Typography';
import {Icon} from '@components/Icon';
import {Form} from '@components/Form';
import {Button} from '@components/Button';
import {FormattedMessage, useIntl} from 'react-intl';
import {Formik} from 'formik';
import {Input} from '@liveComponents/Field';
import * as Yup from 'yup';
import {useSendLeadMessageMutation} from '@gql/mutation/advert/sendLead.gql';
import {PhoneInput} from '@liveComponents/Field/PhoneInput';
import {Row} from '@components/Row';
import {Col} from '@components/Col';
import {ApolloError} from '@apollo/client';
import {processValidationErrors} from '@utils/apollo';
import Link from 'next/link';
import {useLocale} from '@utils/locale';
import {getLocalizedRoute, routes} from '@utils/routes';
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3';
import {useToaster} from '@utils/toast';
import {RecaptchaClaim} from "@liveComponents/RecaptchaClaim";

export type ContactOwnerLeadModalProps = {
    advertId: Scalars['ID'];
    show: boolean;
    onHide: () => any;
};

export const ContactOwnerLeadModal: FC<ContactOwnerLeadModalProps> = ({advertId, show, onHide}) => {
    const intl = useIntl();
    const locale = useLocale();
    const toaster = useToaster();
    const {executeRecaptcha} = useGoogleReCaptcha();
    const [sendMessage, {data}] = useSendLeadMessageMutation();
    const messageLabel = useMemo(
        () => intl.formatMessage({defaultMessage: 'Doptejte se majitele na nemovitost. Napište něco o sobě.'}),
        [intl],
    );

    const processSubmit = useCallback(
        (values, actions) => {
            if (!executeRecaptcha) {
                toaster.toast({
                    message: (
                        <FormattedMessage defaultMessage="Stránka ještě není plně načtena. Vyčkejte prosím jejího načtení a zkuste to znovu." />
                    ),
                    header: <FormattedMessage defaultMessage="Upozornění" />,
                    bg: 'warning',
                    color: 'white',
                    duration: 5000,
                });
                actions.setSubmitting(false);
                return;
            }
            executeRecaptcha('leadForm').then((token) =>
                sendMessage({
                    variables: {
                        message: {
                            advertId: advertId,
                            firstName: values.name,
                            lastName: values.surname,
                            message: values.message,
                            email: values.email,
                            phone: values.phone,
                            recaptcha: token,
                        },
                    },
                })
                    .then((data) => {
                        // TODO
                    })
                    .catch((error: ApolloError) => {
                        const validationErrors = error.graphQLErrors.filter(
                            (error) => error.extensions.category === 'arguments_validation_error',
                        );

                        if (error.graphQLErrors.length === validationErrors.length) {
                            return processValidationErrors(validationErrors, values, actions.setFieldError);
                        }

                        throw error;
                    })
                    .finally(() => {
                        actions.setSubmitting(false);
                    }),
            );
        },
        [advertId, sendMessage, toaster, executeRecaptcha],
    );

    const requiredMessage = useMemo(() => intl.formatMessage({defaultMessage: 'Povinné pole'}), [intl]);

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton closeLabel="Zavřít">
                <Modal.Title>
                    <Typography as="h2" variant="subheadline" className="mb-0">
                        {data?.sendAdvertLead?.id ? (
                            <FormattedMessage defaultMessage="Zpráva byla odeslána" />
                        ) : (
                            <FormattedMessage defaultMessage="Reagovat na inzerát" />
                        )}
                    </Typography>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {!data?.sendAdvertLead?.id && (
                    <>
                        <Typography as="p" textAlign="center" color="grey-dark" className="mb-8">
                            <Icon.MessageText size="xl" />
                        </Typography>

                        <Typography as="h3" textAlign="center" className="mb-5">
                            <FormattedMessage defaultMessage="Napište zprávu" />
                        </Typography>

                        <Typography as="p" textAlign="center" className="mb-8">
                            <FormattedMessage defaultMessage="Zprávu si poté zobrazíte ve svém profilu v sekci Zprávy." />
                        </Typography>

                        <Formik
                            onSubmit={processSubmit}
                            initialValues={{
                                message: '',
                                name: '',
                                surname: '',
                                phone: '',
                                email: '',
                            }}
                            validationSchema={Yup.object({
                                message: Yup.string().required(requiredMessage),
                                name: Yup.string().required(requiredMessage),
                                surname: Yup.string().required(requiredMessage),
                                phone: Yup.string()
                                    .phone('CZ', intl.formatMessage({defaultMessage: 'Neplatný formát'}))
                                    .required(requiredMessage),
                                email: Yup.string()
                                    .email(intl.formatMessage({defaultMessage: 'Neplatný email'}))
                                    .required(requiredMessage),
                            })}
                        >
                            {(props) => (
                                <Form onSubmit={props.handleSubmit}>
                                    <Input name="name" label={intl.formatMessage({defaultMessage: 'Jméno'})} className="mb-3" />

                                    <Input name="surname" label={intl.formatMessage({defaultMessage: 'Příjmení'})} className="mb-3" />

                                    <Input name="email" label={intl.formatMessage({defaultMessage: 'E-mail'})} className="mb-3" />

                                    <PhoneInput name="phone" label={intl.formatMessage({defaultMessage: 'Telefon'})} className="mb-3" />

                                    <Input name="message" label={messageLabel} multiline={true} rows={5} className="mb-8" />

                                    <RecaptchaClaim textAlign={"center"}/>
                                    <Button type="submit" className="w-100" disabled={props.isSubmitting}>
                                        <FormattedMessage defaultMessage="Poslat zprávu" />
                                    </Button>
                                </Form>
                            )}
                        </Formik>
                    </>
                )}

                {data?.sendAdvertLead?.id && (
                    <>
                        <Typography as="p" textAlign="center" color="grey-dark" className="mb-8">
                            <Icon.Star size="xl" />
                        </Typography>

                        <Typography as="h3" textAlign="center" className="mb-5">
                            <FormattedMessage defaultMessage="Vaše zpráva byla úspěšně odeslána!" />
                        </Typography>

                        <Typography as="p" textAlign="center" className="mb-8">
                            <FormattedMessage defaultMessage="Odesláním zprávy jste zahájili komunikaci s majitelem nemovitosti. Všechnu vaši komunikaci najdete nyní v sekci Zprávy." />
                        </Typography>

                        <Row className="justify-content-center gy-3 gx-3 gx-sm-6">
                            <Col xs="auto">
                                <Button variant="outline-dark" onClick={onHide}>
                                    <FormattedMessage defaultMessage="Zavřít" />
                                </Button>
                            </Col>
                            <Col xs="auto">
                                <Link href={getLocalizedRoute(routes.messages, locale)} passHref>
                                    <Button>
                                        <FormattedMessage defaultMessage="Přejít do zpráv" />
                                    </Button>
                                </Link>
                            </Col>
                        </Row>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};
